import React, { useState } from "react";


const ESPECIES_SIN_AUTO_GRAVE = ["perro", "gato"];

const LOCALIZACION_NIVEL = {
  "Cabeza, cara, cuello": 3,
  "Manos, dedos": 3,
  "Tronco": 2,
  "Miembros superiores": 2,
  "Miembros inferiores": 2,
  "Pies, dedos": 3,
  "Genitales": 3,
};

const CONDUCTAS = {
  3: {
    clase: "grave",
    color: "#e0483e",
    titulo: "Exposición Grave",
    acciones: [
      "Lavado de la herida.",
      "Vacuna antirrábica.",
      "Suero antirrábico.",
      "Diligenciar ficha epidemiológica Cod. 300.",
    ],
  },
  2: {
    clase: "leve",
    color: "#f0a83c",
    titulo: "Exposición Leve",
    acciones: [
      "Lavado de la herida.",
      "Vacuna antirrábica.",
      "Diligenciar ficha epidemiológica Cod. 300.",
    ],
  },
  1: {
    clase: "no-exp",
    color: "#3fb27f",
    titulo: "No Exposición",
    acciones: [
      "Lavado de la herida.",
      "Observación del animal durante 10 días.",
      "Diligenciar ficha epidemiológica Cod. 300.",
    ],
  },
};

const ESTADO_INICIAL = {
  especie: "",
  signos: "",
  ubicacion: "",
  tipoLesion: "",
  profundidad: "",
  localizacion: "",
};

function esEspecieAltoRiesgo(valor) {
  return valor !== "" && !ESPECIES_SIN_AUTO_GRAVE.includes(valor);
}

export default function ClasificacionAPTR() {
  const [form, setForm] = useState(ESTADO_INICIAL);
  const [modal, setModal] = useState(null); // { nivel, automatica, mensaje }

  const { especie, signos, ubicacion, tipoLesion, profundidad, localizacion } = form;

  // ---- Estados habilitados, derivados directamente de las respuestas ----
  const especieAltoRiesgo = esEspecieAltoRiesgo(especie);
  const habilitarSignos = especie !== "" && !especieAltoRiesgo;
  const habilitarUbicacion = habilitarSignos && signos === "No";
  const habilitarTipoLesion = habilitarUbicacion && ubicacion === "Perdido";
  const habilitarProfundidad = habilitarTipoLesion && tipoLesion === "Única";
  const habilitarLocalizacion = habilitarProfundidad && profundidad === "Superficial";

  // ---- Nota informativa bajo la última pregunta habilitada ----
  function obtenerNota() {
    if (especie === "") return null;
    if (especieAltoRiesgo)
      return "La especie seleccionada clasifica automáticamente como <strong>Exposición Grave</strong>. Las demás preguntas se deshabilitan.";
    if (signos === "Si")
      return "Signos de rabia positivos: clasifica como <strong>Exposición Grave</strong>. Las preguntas siguientes se deshabilitan.";
    if (signos === "") return null;
    if (ubicacion === "Observable")
      return "Animal observable: clasifica como <strong>No Exposición</strong>. Las preguntas siguientes se deshabilitan.";
    if (ubicacion === "") return null;
    if (tipoLesion === "Múltiple")
      return "Lesión múltiple: clasifica como <strong>Exposición Grave</strong>. Las preguntas siguientes se deshabilitan.";
    if (tipoLesion === "") return null;
    if (profundidad === "Profunda")
      return "Lesión profunda: clasifica como <strong>Exposición Grave</strong>. La pregunta de localización se deshabilita.";
    return null;
  }

  function abrirModal(nivel, automatica, mensaje) {
    if (nivel == null) return;
    setModal({ nivel, automatica, mensaje });
  }

  function cerrarModal() {
    setModal(null);
  }

  // ---- Handlers: cada uno limpia las respuestas siguientes y dispara
  //      el modal automático cuando la respuesta ya define el resultado ----
  function handleEspecie(valor) {
    setForm({ ...ESTADO_INICIAL, especie: valor });
    if (esEspecieAltoRiesgo(valor)) {
      abrirModal(3, true, "La especie seleccionada clasifica automáticamente como Exposición Grave.");
    }
  }

  function handleSignos(valor) {
    setForm((prev) => ({ ...prev, signos: valor, ubicacion: "", tipoLesion: "", profundidad: "", localizacion: "" }));
    if (valor === "Si") {
      abrirModal(3, true, "Signos de rabia positivos en el animal.");
    }
  }

  function handleUbicacion(valor) {
    setForm((prev) => ({ ...prev, ubicacion: valor, tipoLesion: "", profundidad: "", localizacion: "" }));
    if (valor === "Observable") {
      abrirModal(1, true, "El animal agresor se encuentra observable.");
    }
  }

  function handleTipoLesion(valor) {
    setForm((prev) => ({ ...prev, tipoLesion: valor, profundidad: "", localizacion: "" }));
    if (valor === "Múltiple") {
      abrirModal(3, true, "Se registró una lesión múltiple.");
    }
  }

  function handleProfundidad(valor) {
    setForm((prev) => ({ ...prev, profundidad: valor, localizacion: "" }));
    if (valor === "Profunda") {
      abrirModal(3, true, "Se registró una lesión profunda.");
    }
  }

  function handleLocalizacion(valor) {
    setForm((prev) => ({ ...prev, localizacion: valor }));
  }

  function calcularNivel() {
    if (especieAltoRiesgo) return 3;
    if (signos === "Si") return 3;
    if (ubicacion === "Observable") return 1;
    if (tipoLesion === "Múltiple") return 3;
    if (profundidad === "Profunda") return 3;
    if (localizacion) return LOCALIZACION_NIVEL[localizacion];
    return null;
  }

  function handleSubmit(e) {
    e.preventDefault();
    abrirModal(calcularNivel(), false, null);
  }

  function handleLimpiar() {
    setForm(ESTADO_INICIAL);
    cerrarModal();
  }

  const nota = obtenerNota();
  const resultadoModal = modal ? CONDUCTAS[modal.nivel] : null;

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <div className="text-center mb-4">
        <h1 className="h3 fw-bold" style={{ color: "#1a8ec2" }}>
          Clasificación APTR
        </h1>
        <p className="text-muted small mb-0">
          Agresiones por animales potencialmente transmisores de rabia (APTR) — Herramienta de apoyo a la clasificación
        </p>
      </div>

      {nota && (
        <div
          className="alert mb-3"
          style={{ background: "#fff4e6", border: "1px solid #f0a83c", color: "#8a5a10", fontSize: 13 }}
          dangerouslySetInnerHTML={{ __html: nota }}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Pregunta numero={1} texto="Especie agresora">
          <select
            className="form-select"
            required
            value={especie}
            onChange={(e) => handleEspecie(e.target.value)}
          >
            <option value="">Seleccione una opción</option>
            <option value="perro">Perro</option>
            <option value="gato">Gato</option>
            <option value="Bovino">Bovino</option>
            <option value="Equino">Equino</option>
            <option value="porcino">Porcino</option>
            <option value="Murciélago">Murciélago</option>
            <option value="zorro">Zorro</option>
            <option value="Mico">Mico</option>
            <option value="Otros silvestres">Otros silvestres</option>
            <option value="ovino">Ovino</option>
            <option value="Grandes roedores">Grandes roedores</option>
          </select>
        </Pregunta>

        <Pregunta numero={2} texto="Signos de rabia en el animal" deshabilitada={!habilitarSignos}>
          <select
            className="form-select"
            required={habilitarSignos}
            disabled={!habilitarSignos}
            value={signos}
            onChange={(e) => handleSignos(e.target.value)}
          >
            <option value="">Seleccione una opción</option>
            <option value="Si">Sí</option>
            <option value="No">No</option>
          </select>
        </Pregunta>

        <Pregunta numero={3} texto="Ubicación del animal" deshabilitada={!habilitarUbicacion}>
          <select
            className="form-select"
            required={habilitarUbicacion}
            disabled={!habilitarUbicacion}
            value={ubicacion}
            onChange={(e) => handleUbicacion(e.target.value)}
          >
            <option value="">Seleccione una opción</option>
            <option value="Observable">Observable</option>
            <option value="Perdido">Perdido</option>
          </select>
        </Pregunta>

        <Pregunta numero={4} texto="Tipo de lesión" deshabilitada={!habilitarTipoLesion}>
          <select
            className="form-select"
            required={habilitarTipoLesion}
            disabled={!habilitarTipoLesion}
            value={tipoLesion}
            onChange={(e) => handleTipoLesion(e.target.value)}
          >
            <option value="">Seleccione una opción</option>
            <option value="Única">Única</option>
            <option value="Múltiple">Múltiple</option>
          </select>
        </Pregunta>

        <Pregunta numero={5} texto="Profundidad de la lesión" deshabilitada={!habilitarProfundidad}>
          <select
            className="form-select"
            required={habilitarProfundidad}
            disabled={!habilitarProfundidad}
            value={profundidad}
            onChange={(e) => handleProfundidad(e.target.value)}
          >
            <option value="">Seleccione una opción</option>
            <option value="Superficial">Superficial</option>
            <option value="Profunda">Profunda</option>
          </select>
        </Pregunta>

        <Pregunta numero={6} texto="Localización de la lesión" deshabilitada={!habilitarLocalizacion}>
          <select
            className="form-select"
            required={habilitarLocalizacion}
            disabled={!habilitarLocalizacion}
            value={localizacion}
            onChange={(e) => handleLocalizacion(e.target.value)}
          >
            <option value="">Seleccione una opción</option>
            <option value="Cabeza, cara, cuello">Cabeza, cara, cuello</option>
            <option value="Manos, dedos">Manos, dedos</option>
            <option value="Tronco">Tronco</option>
            <option value="Miembros superiores">Miembros superiores</option>
            <option value="Miembros inferiores">Miembros inferiores</option>
            <option value="Pies, dedos">Pies, dedos</option>
            <option value="Genitales">Genitales</option>
          </select>
        </Pregunta>

        <div className="d-flex gap-2 mt-3">
          <button type="submit" className="btn btn-primary flex-grow-1" style={{ background: "#28b6ea", border: "none" }}>
            Clasificar
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={handleLimpiar}>
            Limpiar casillas
          </button>
        </div>
      </form>

      <p className="text-center text-muted mt-4" style={{ fontSize: 12 }}>
        Esta clasificación es una herramienta de apoyo y no reemplaza el criterio clínico
        <br />
        del profesional de salud tratante.
      </p>

      {modal && resultadoModal && (
        <ModalResultado
          resultado={resultadoModal}
          automatica={modal.automatica}
          mensaje={modal.mensaje}
          onCerrar={cerrarModal}
        />
      )}
    </div>
  );
}

function Pregunta({ numero, texto, deshabilitada, children }) {
  return (
    <div
      className="card mb-3"
      style={{
        borderLeft: "4px solid #28b6ea",
        opacity: deshabilitada ? 0.55 : 1,
        transition: "opacity .15s ease",
      }}
    >
      <div className="card-body">
        <label className="fw-semibold d-block mb-2" style={{ fontSize: 15 }}>
          <span
            className="d-inline-flex align-items-center justify-content-center me-2"
            style={{ width: 22, height: 22, borderRadius: "50%", background: "#28b6ea", color: "#fff", fontSize: 12 }}
          >
            {numero}
          </span>
          {texto}
        </label>
        {children}
      </div>
    </div>
  );
}

function ModalResultado({ resultado, automatica, mensaje, onCerrar }) {
  return (
    <div
      className="modal d-block"
      style={{ background: "rgba(20,20,20,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onCerrar(); }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 12 }}>
          <div style={{ background: resultado.color, color: "#fff", borderRadius: "12px 12px 0 0", padding: "16px 20px" }}>
            {automatica && (
              <span
                className="d-inline-block mb-1"
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  background: "rgba(255,255,255,0.25)",
                  padding: "2px 8px",
                  borderRadius: 20,
                }}
              >
                Clasificación automática
              </span>
            )}
            <h2 className="h5 mb-0">{resultado.titulo}</h2>
          </div>
          <div className="modal-body">
            {automatica && mensaje && (
              <p className="text-muted" style={{ fontSize: 13 }}>{mensaje}</p>
            )}
            <p className="text-uppercase text-muted mb-1" style={{ fontSize: 12, letterSpacing: 0.6 }}>
              Conducta recomendada
            </p>
            <ul className="mb-0" style={{ paddingLeft: 18 }}>
              {resultado.acciones.map((accion, i) => (
                <li key={i} style={{ fontSize: 14, marginBottom: 6 }}>{accion}</li>
              ))}
            </ul>
          </div>
          <div className="modal-footer border-0 pt-0">
            <button
              type="button"
              className="btn w-100"
              style={{ background: "#28b6ea", color: "#fff" }}
              onClick={onCerrar}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
